import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * WorkoutPrintPage — A4 printer-friendly view of a workout.
 *
 * Renders at /workout/:id/print. Auth-protected via existing axios
 * defaults (the calling page is already inside the trainer/client app).
 *
 * Layout: clean header + exercise table with blank columns for the client
 * to record actual weight / reps / RPE in pen.
 */
const WorkoutPrintPage = () => {
  const { id } = useParams();
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [wRes, profRes] = await Promise.all([
          axios.get(`${API}/workouts/${id}`),
          axios.get(`${API}/auth/me`).catch(() => null),
        ]);
        if (!cancelled) {
          setWorkout({ ...wRes.data, _viewer: profRes?.data });
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'No se pudo cargar la rutina');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Auto-trigger print dialog once content loads (small delay so images render)
  useEffect(() => {
    if (!loading && workout) {
      const t = setTimeout(() => {
        try { window.print(); } catch (e) { console.warn('Auto-print failed:', e); }
      }, 800);
      return () => clearTimeout(t);
    }
  }, [loading, workout]);

  if (loading) {
    return <div style={{ padding: 24, fontFamily: 'sans-serif' }}>Cargando rutina...</div>;
  }
  if (error || !workout) {
    return (
      <div style={{ padding: 24, fontFamily: 'sans-serif', color: '#b91c1c' }}>
        {error || 'Rutina no encontrada'}
      </div>
    );
  }

  const exercises = workout.exercises || [];
  const isFlexible = workout.mode === 'flexible';

  const fmtDate = (s) => {
    if (!s) return '';
    try { return new Date(s).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return s; }
  };

  return (
    <>
      {/* Print-specific styles — keep all rules inline so this page works
          even outside the bundled CSS (the route can be opened in a new tab). */}
      <style>{`
        @page { size: A4; margin: 14mm 12mm; }
        html, body, #root { background: #fff; color: #111; }
        .print-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
          color: #111;
          background: #fff;
          padding: 18px 22px;
          max-width: 780px;
          margin: 0 auto;
        }
        .print-toolbar {
          display: flex; gap: 8px; justify-content: flex-end;
          margin-bottom: 12px; padding: 8px;
          background: #f3f4f6; border-radius: 8px;
        }
        .print-toolbar button {
          padding: 8px 14px; border-radius: 6px; border: 1px solid #d1d5db;
          background: #fff; cursor: pointer; font-weight: 600;
        }
        .print-toolbar button.primary {
          background: #111; color: #fff; border-color: #111;
        }
        @media print {
          .print-toolbar { display: none; }
          .print-container { padding: 0; max-width: none; }
        }
        .ws-header {
          border-bottom: 2px solid #111;
          padding-bottom: 12px; margin-bottom: 18px;
          display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
        }
        .ws-title { font-size: 22px; font-weight: 700; margin: 0 0 4px 0; }
        .ws-subtitle { font-size: 13px; color: #4b5563; margin: 0; }
        .ws-meta { text-align: right; font-size: 12px; color: #4b5563; line-height: 1.4; }
        .ws-meta strong { color: #111; }
        .ws-pill {
          display: inline-block; padding: 2px 8px; border-radius: 999px;
          background: #fef3c7; color: #92400e; font-size: 11px; font-weight: 600;
          border: 1px solid #fcd34d;
        }
        .ws-notes {
          background: #f9fafb; border-left: 4px solid #111;
          padding: 10px 14px; margin: 0 0 18px 0; border-radius: 4px;
          font-size: 13px; line-height: 1.5;
        }
        .ws-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .ws-table th, .ws-table td {
          border: 1px solid #d1d5db; padding: 8px 6px;
          text-align: left; vertical-align: top;
        }
        .ws-table th {
          background: #f3f4f6; font-weight: 600;
          font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em;
        }
        .ws-table td.num { text-align: center; font-weight: 600; width: 36px; }
        .ws-table td.target { width: 90px; text-align: center; }
        .ws-table td.blank { background: #fafafa; }
        .ws-exname { font-weight: 600; }
        .ws-exhint { font-size: 11px; color: #6b7280; margin-top: 2px; }
        .ws-footer {
          margin-top: 22px; padding-top: 10px;
          border-top: 1px solid #e5e7eb;
          font-size: 11px; color: #6b7280;
          display: flex; justify-content: space-between;
        }
        .ws-table tr { page-break-inside: avoid; }
      `}</style>

      <div className="print-container">
        <div className="print-toolbar">
          <button onClick={() => window.close()} data-testid="print-close-btn">Cerrar</button>
          <button className="primary" onClick={() => window.print()} data-testid="print-btn">
            🖨️ Imprimir / PDF
          </button>
        </div>

        {/* Header */}
        <div className="ws-header">
          <div>
            <h1 className="ws-title">
              {workout.template_name || 'Rutina de entrenamiento'}
            </h1>
            <p className="ws-subtitle">
              {isFlexible ? '🏠 Rutina libre · sin cita' : '📅 Sesión programada'}
              {workout.template_focus ? ` · ${workout.template_focus}` : ''}
            </p>
            {isFlexible && (workout.available_from || workout.available_until) && (
              <p className="ws-subtitle" style={{ marginTop: 4 }}>
                Disponible: {fmtDate(workout.available_from) || '—'}
                {' → '}
                {fmtDate(workout.available_until) || '—'}
              </p>
            )}
          </div>
          <div className="ws-meta">
            {isFlexible && <div><span className="ws-pill">🏠 Rutina libre</span></div>}
            <div style={{ marginTop: 6 }}>
              <strong>Fecha:</strong> {fmtDate(workout.date) || '—'}
            </div>
            <div>
              <strong>Ejercicios:</strong> {exercises.length}
            </div>
          </div>
        </div>

        {/* Trainer notes */}
        {workout.trainer_notes && (
          <div className="ws-notes">
            <strong>Notas del entrenador:</strong> {workout.trainer_notes}
          </div>
        )}

        {/* Exercise table */}
        <table className="ws-table">
          <thead>
            <tr>
              <th style={{ width: 28 }}>#</th>
              <th>Ejercicio</th>
              <th className="target">Series × Reps</th>
              <th style={{ width: 60 }}>Descanso</th>
              <th>Peso real</th>
              <th>Reps reales</th>
              <th>RPE / Notas</th>
            </tr>
          </thead>
          <tbody>
            {exercises.map((ex, i) => {
              const sets = ex.target_sets || ex.sets || 3;
              const reps = ex.target_reps || ex.reps || '—';
              const rest = ex.rest_time ? `${ex.rest_time}s` : '—';
              const wt = ex.target_weight || ex.weight;
              return (
                <tr key={i}>
                  <td className="num">{i + 1}</td>
                  <td>
                    <div className="ws-exname">{ex.exercise_name_es || ex.exercise_name || 'Ejercicio'}</div>
                    {ex.notes && (<div className="ws-exhint">{ex.notes}</div>)}
                    {ex.is_superset && (<div className="ws-exhint">↳ Superserie {ex.superset_group || ''}</div>)}
                  </td>
                  <td className="target">
                    {sets} × {reps}
                    {wt ? <div className="ws-exhint">objetivo: {wt} kg</div> : null}
                  </td>
                  <td className="target">{rest}</td>
                  <td className="blank">&nbsp;</td>
                  <td className="blank">&nbsp;</td>
                  <td className="blank">&nbsp;</td>
                </tr>
              );
            })}
            {exercises.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#6b7280' }}>
                  Esta rutina aún no tiene ejercicios.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="ws-footer">
          <span>Bétera Training Center</span>
          <span>Generado: {new Date().toLocaleString('es-ES')}</span>
        </div>
      </div>
    </>
  );
};

export default WorkoutPrintPage;
